import type { Column, GridApi, IRowNode } from 'ag-grid-enterprise'
import consola from 'consola'
import moment from 'moment'
import type { StcsCompActualsResValuesItem } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import { MethodologyEnum } from '~/protos/gen/stcs_comp_plan_cfg.entity'
import type { StcsCompPlanResValues } from '~/protos/gen/stcs_comp_plan_res_values.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import { cleanString, getDynamicDefaultRefId } from '~/services/commons.service'
import { useCtxStore } from '~/stores/ctx.store.ts'
import { createPlanningViewStore } from '~/stores/planningview.store'

const COL_PREFIX_REGEX = /^col_/
const UNDERSCORE_REGEX = /_/g
const CTX_LINE_REGEX = /_ctx_line.*$/
const MONTH_COLUMN_REGEX = /^col_\d{4}_\d{2}$/

export const EXCLUDED_ANNUAL_GROWTH_LEVELS = ['annual_growth_based_1']

const translationMap = {
  manual_change_based: {
    en: 'Manual change and base',
    de: 'Manueller Wechsel und Sockel',
  },
  monthly_growth_based: {
    en: 'Monthly growth rate & base',
    de: 'Monatliche Wachstumsrate und Basis',
  },
  annual_growth_based_1: {
    en: 'Year to Go (current FY)',
    de: 'Verbleibendes Jahr (aktuelles Geschäftsjahr)',
  },
  annual_growth_based_2: {
    en: 'Yearly growth & base',
    de: 'Jährliche Wachstumsrate und Basis',
  },
}

export function getAllPlanCols(gridApi: GridApi<PlanningRowData> | null): Column[] {
  return (
    gridApi?.getAllDisplayedColumns()?.filter((col) => {
      const hc = getHeaderClass(col.getColDef())
      return hc.includes('header-plan')
    }) ?? []
  )
}

export function toMonthColId(prefixedKey: string): string {
  return prefixedKey.replace(COL_PREFIX_REGEX, '').replace(UNDERSCORE_REGEX, '-')
}

export const isValidPlanColumn = (columnMonth: string, startMonth: number, maxMonths: number, gridApi: GridApi<PlanningRowData> | null) => {
  if (!columnMonth) return false

  const firstPlanCol = getFirstPlanCol(gridApi)
  const firstPlanYear = Number.parseInt(getColumnDateStr(firstPlanCol)?.split('-')[0] ?? '', 10)
  const columnYear = Number.parseInt(columnMonth.split('-')[0], 10)
  const columnMonthNum = Number.parseInt(columnMonth.split('-')[1], 10)

  // Get fiscal year-end month from company data for fiscal year-aware validation
  const fyeMonth = getCompEndMonth()

  // For fiscal year-end months other than December, we need to adjust validation
  // Check if we're in the same fiscal year as the first plan column
  const firstPlanFiscalYear = getFirstPlanCol(gridApi) ? getFiscalYear(new Date(firstPlanYear, 0, 1), fyeMonth) : firstPlanYear
  const columnFiscalYear = getFiscalYear(new Date(columnYear, columnMonthNum - 1, 1), fyeMonth)

  // Basic validation: same fiscal year and within month range
  const isSameFiscalYear = columnFiscalYear === firstPlanFiscalYear
  const isWithinMonthRange = columnMonthNum >= startMonth && columnMonthNum < startMonth + maxMonths

  const result = isSameFiscalYear && isWithinMonthRange

  return result
}

export function getFirstPlanCol(gridApi: GridApi<PlanningRowData> | null) {
  const allCols = gridApi?.getAllDisplayedColumns() || []
  return allCols.filter((c) => getHeaderClass(c.getColDef()) === 'header-plan-ytd-begin')[0]
}

export function isAllowed(node: IRowNode<PlanningRowData> | null) {
  return !!node?.data?.rowClass && ['custom-row', 'sub-custom-row', 'tag-row', 'account-row'].includes(node.data.rowClass)
}

export function getLastActualCol(gridApi: GridApi<PlanningRowData> | null) {
  const allCols = gridApi?.getAllDisplayedColumns() || []
  const filteredCols = allCols.filter((c) => getHeaderClass(c.getColDef()).includes('header-actuals'))
  const lastActCol = filteredCols.pop()

  return lastActCol
}

export const getAvailableYearsLength = (): number => {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  if (!planningStore.selectedPlan) return 0

  const dateIsoUtc = getDisplayDateFromPlanningBegin(planningStore.selectedPlan?.fpna_begin_date_string) || ''
  const planUntilDateIsoUtc = planningStore.selectedPlan?.fpna_end_date_string || ''
  const startDate = new Date(dateIsoUtc) // e.g., "2024-06"
  const endDate = new Date(planUntilDateIsoUtc) // e.g., "2027-12"

  // Get fiscal year-end month from company data
  const fyeMonth = getCompEndMonth()

  // Calculate fiscal years for plan period
  const startYear = startDate.getFullYear()
  const startMonth = startDate.getMonth() + 1 // getMonth() is 0-based
  const endYear = endDate.getFullYear()
  const endMonth = endDate.getMonth() + 1

  // Determine fiscal year start based on fiscal year-end month
  // If start date is after FYE month, it's in the next fiscal year
  const startFiscalYear = startMonth > fyeMonth ? startYear + 1 : startYear
  const endFiscalYear = endMonth > fyeMonth ? endYear + 1 : endYear

  const availableYears = Math.max(0, endFiscalYear - startFiscalYear + 1)

  return availableYears
}

export function refreshFormulaCells(rowId: string, gridApi: GridApi<PlanningRowData> | null) {
  if (!gridApi) return

  // Find the row node
  const rowNode = gridApi.getRowNode(rowId)
  if (!rowNode) return

  // Get all actual columns
  const actualCols = getAllActualColumns(gridApi)
  if (actualCols.length === 0) return

  // Get last three actual columns for refresh
  const lastThreeStartIdx = Math.max(0, actualCols.length - 3)
  const lastThreeActualCols = actualCols.slice(lastThreeStartIdx)

  // Refresh only the target cells
  gridApi.refreshCells({
    rowNodes: [rowNode],
    columns: lastThreeActualCols,
  })
}

export const isPlanColumn = (colId: string | null, gridApi: GridApi<PlanningRowData> | null): boolean => {
  if (!colId) return false
  if (!gridApi) return false
  const col = gridApi.getColumn(colId)
  const colDef = col?.getColDef()
  if (!colDef) return false
  const hc = getHeaderClass(colDef)
  return hc.includes('header-plan')
}

export function refreshGrid(selectedRowId: string | null, gridApi: GridApi<PlanningRowData> | null) {
  if (selectedRowId) {
    const rowNode = gridApi?.getRowNode(selectedRowId)
    if (rowNode) {
      gridApi?.refreshCells({
        rowNodes: [rowNode],
        force: true,
      })
    }
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function buildPercentValues(rawRowId: string, refRowId: string, ctxRowId: string, mapKey: 'pnl' | 'bs' | 'cf') {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const fsdOrg = ctxStore.ref_fsd_map[ctxStore.active_org_id]
  const compId = ctxStore.active_comp_id

  if (!fsdOrg) return {}

  const { selectedPlan, activePlanningStatement: fstmt } = planningStore
  const lastAct = selectedPlan?.fpna_begin_date_string ? new Date(getDisplayDateFromPlanningBegin(selectedPlan.fpna_begin_date_string)) : null
  const planTil = selectedPlan?.fpna_end_date_string ? new Date(selectedPlan.fpna_end_date_string) : null

  // Get row data based on financial statement
  const rowData = getSelectedPlanRowDataByFstmt(fstmt)

  const rawRowData = rowData?.find((r) => r.row_id === rawRowId)
  let refRowData: PlanningRowData | undefined
  if (mapKey === 'pnl') {
    refRowData = fsdOrg.pnl_list?.find((r) => r.row_id === refRowId)
  } else if (mapKey === 'bs') {
    refRowData = fsdOrg.bs_list?.find((r) => r.row_id === refRowId)
  } else {
    refRowData = ctxStore.ref_comp_map[compId]?.ref_comp_actuals_res_fstmt?.cf_list?.find((r) => r.row_id === refRowId)
  }

  const rawRowInvertFactor = rawRowData?.options?.invert_sum_value as number
  const refRowInvertFactor = refRowData?.options?.invert_sum_value as number

  // Compute actuals and plan maps
  const actualsMap = computed(() => ({
    pnl: sliceValuesMap(planningStore.valuesMapPnl, 'actuals'),
    bs: sliceValuesMap(planningStore.valuesMapBs, 'actuals'),
    cf: sliceValuesMap(planningStore.valuesMapCf, 'actuals'),
  })).value

  const planMap = computed(() => ({
    pnl: sliceValuesMap(planningStore.valuesMapPnl, 'plan'),
    bs: sliceValuesMap(planningStore.valuesMapBs, 'plan'),
    cf: sliceValuesMap(planningStore.valuesMapCf, 'plan'),
  })).value

  const ctxValues = fstmt === StcsFstmtEnum.SFSE_PNL ? actualsMap.pnl : fstmt === StcsFstmtEnum.SFSE_BS ? actualsMap.bs : actualsMap.cf
  const planValues = fstmt === StcsFstmtEnum.SFSE_PNL ? planMap.pnl : fstmt === StcsFstmtEnum.SFSE_BS ? planMap.bs : planMap.cf
  const refPrefix = refRowId.split('_')[0] as 'pnl' | 'bs' | 'cf'

  const actRows = actualsMap[refPrefix] ?? {}
  const planRows = planMap[refPrefix] ?? {}
  const actCtx = ctxValues?.[rawRowId]?.values_map ?? {}
  const actRef = actRows[refRowId]?.values_map ?? {}
  const planCtx = planValues?.[rawRowId]?.values_map ?? {}
  const planRef = planRows[refRowId]?.values_map ?? {}

  const valuesMap: Record<string, number> = {}
  const months = new Set([...Object.keys(actCtx), ...Object.keys(actRef), ...Object.keys(planCtx), ...Object.keys(planRef)])

  for (const month of months) {
    const date = new Date(`${month}-01`)
    // biome-ignore lint/style/useSingleVarDeclarator: <explanation>
    let ctx: number | undefined, ref: number | undefined

    if (lastAct && date <= lastAct) {
      ctx = actCtx[month]
      ref = actRef[month]
    } else if (planTil && date <= planTil) {
      ctx = planCtx[month]
      ref = planRef[month]
    }

    if (typeof ctx === 'number') ctx *= rawRowInvertFactor
    if (typeof ref === 'number') ref *= refRowInvertFactor

    if (typeof ctx === 'number' && typeof ref === 'number' && ref !== 0 && Math.abs(ref) > 1e-8) {
      valuesMap[month] = ctx / ref
    }
  }

  actRows[ctxRowId] = { values_map: valuesMap }
  planRows[ctxRowId] = { values_map: valuesMap }

  return valuesMap
}

export function sliceValuesMap<T extends { values_map: Record<string, number> }>(obj: Record<string, T>, mapType: 'actuals' | 'plan'): Record<string, T> {
  const planningStore = createPlanningViewStore(useCtxStore().active_comp_id)
  const cutoff = planningStore.selectedPlan?.fpna_begin_date_string ? getDisplayDateFromPlanningBegin(planningStore.selectedPlan.fpna_begin_date_string) : null

  if (!cutoff) return {}

  return Object.fromEntries(
    Object.entries(obj).map(([key, item]) => [
      key,
      {
        ...item,
        values_map: Object.fromEntries(Object.entries(item.values_map).filter(([iso]) => (mapType === 'actuals' ? iso <= cutoff : iso > cutoff))),
      },
    ]),
  )
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function recalcCtxRows(parentNodeData: PlanningRowData | null) {
  if (!parentNodeData) return
  const parentPath = parentNodeData?.path_level

  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const fsdOrg = ctxStore.ref_fsd_map[ctxStore.active_org_id]

  if (!fsdOrg) return {}

  const { activePlanningStatement: fstmt } = planningStore

  // Get row data based on financial statement
  const rowData = getSelectedPlanRowDataByFstmt(fstmt)

  const ctxRows = rowData.filter((r) => r.rowClass === 'total-cml-ctx-line-percent-child' && JSON.stringify(r.path_level.slice(0, parentPath?.length)) === JSON.stringify(parentPath))
  for (const ctx of ctxRows) {
    let newMap: Record<string, number>
    const rawId = ctx.row_id.replace(CTX_LINE_REGEX, '')
    if (ctx.level_id === 'ctx_line_growth') {
      newMap = buildGrowthValues(rawId)
    } else {
      // Use the stored sum_ref from the CTX row if available, otherwise fall back to default
      const referenceId = ctx.sum_ref || getDynamicDefaultRefId(rowData || [], planningStore.activePlanningStatement)
      // Derive mapKey from the referenceId prefix to handle cross-statement references (e.g., BS reference in P&L)
      const refPrefix = referenceId.split('_')[0] as 'pnl' | 'bs' | 'cf'
      const mapKey: 'pnl' | 'bs' | 'cf' = ['pnl', 'bs', 'cf'].includes(refPrefix) ? refPrefix : planningStore.activePlanningStatement === StcsFstmtEnum.SFSE_PNL ? 'pnl' : 'bs'
      newMap = buildPercentValues(rawId, referenceId, ctx.row_id, mapKey)
    }
    if (planningStore.activePlanningStatement === StcsFstmtEnum.SFSE_PNL && planningStore.selectedPlanState?.valuesMapPnl) {
      planningStore.selectedPlanState.valuesMapPnl[ctx.row_id] = { values_map: newMap }
    } else if (planningStore.selectedPlanState?.valuesMapBs) {
      planningStore.selectedPlanState.valuesMapBs[ctx.row_id] = { values_map: newMap }
    }
  }
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function buildGrowthValues(rowId: string): Record<string, number> {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const fsdOrg = ctxStore.ref_fsd_map[ctxStore.active_org_id]

  if (!fsdOrg) return {}

  const { activePlanningStatement: fstmt } = planningStore

  // Compute actuals and plan maps
  const actualsMap = computed(() => ({
    pnl: sliceValuesMap(planningStore.valuesMapPnl, 'actuals'),
    bs: sliceValuesMap(planningStore.valuesMapBs, 'actuals'),
    cf: sliceValuesMap(planningStore.valuesMapCf, 'actuals'),
  })).value

  const planMap = computed(() => ({
    pnl: sliceValuesMap(planningStore.valuesMapPnl, 'plan'),
    bs: sliceValuesMap(planningStore.valuesMapBs, 'plan'),
    cf: sliceValuesMap(planningStore.valuesMapCf, 'plan'),
  })).value

  const lastActISO = getDisplayDateFromPlanningBegin(planningStore.selectedPlan?.fpna_begin_date_string)
  const planTilISO = planningStore.selectedPlan?.fpna_end_date_string
  const lastAct = lastActISO ? new Date(lastActISO) : null
  const planTil = planTilISO ? new Date(planTilISO) : null

  const beginData = computed(() => {
    return useCtxStore().ref_comp_map[useCtxStore().active_comp_id].ref_comp_actuals_res_values?.meta?.fpna_begin_date_string
  })
  const endData = computed(() => {
    return useCtxStore().ref_comp_map[useCtxStore().active_comp_id].ref_comp_actuals_res_values?.meta?.fpna_end_date_string
  })
  const rangeStr = computed(() => {
    return `${beginData.value},${endData.value}`
  })
  const [firstActual] = rangeStr.value.split(',')

  const ctxActuals = fstmt === StcsFstmtEnum.SFSE_PNL ? actualsMap?.pnl : fstmt === StcsFstmtEnum.SFSE_BS ? actualsMap?.bs : actualsMap?.cf
  const ctxPlan = fstmt === StcsFstmtEnum.SFSE_PNL ? planMap?.pnl : fstmt === StcsFstmtEnum.SFSE_BS ? planMap?.bs : planMap?.cf

  const actMap = ctxActuals?.[rowId]?.values_map ?? {}
  const planResMap = ctxPlan?.[rowId]?.values_map ?? {}

  const months = Array.from(new Set([...Object.keys(actMap), ...Object.keys(planResMap)])).sort()

  const firstIndex = months.indexOf(firstActual)
  const startIdx = firstIndex >= 0 ? firstIndex + 1 : 1

  const growth: Record<string, number> = {}

  for (let i = startIdx; i < months.length; i++) {
    const curKey = months[i]
    const prevKey = months[i - 1]
    const curDate = new Date(`${curKey}-01`)
    const prevDate = new Date(`${prevKey}-01`)

    let curVal: number | undefined
    let prevVal: number | undefined

    if (lastAct && lastAct && curDate <= lastAct && prevDate <= lastAct) {
      curVal = actMap[curKey]
      prevVal = actMap[prevKey]
    } else if (lastAct && planTil && curDate > lastAct && curDate <= planTil) {
      curVal = planResMap[curKey]
      prevVal = prevDate > lastAct ? planResMap[prevKey] : actMap[prevKey]
    }

    if (typeof curVal === 'number' && typeof prevVal === 'number' && prevVal !== 0) {
      growth[curKey] = curVal / prevVal - 1
    }
  }

  return growth
}

export const getSelectedPlanRowDataByFstmt = (fstmt: StcsFstmtEnum): PlanningRowData[] => {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const selectedPlanData = planningStore.selectedPlanState
  if (!selectedPlanData) {
    return []
  }
  switch (fstmt) {
    case StcsFstmtEnum.SFSE_PNL:
      return selectedPlanData.pnlList
    case StcsFstmtEnum.SFSE_BS:
      return selectedPlanData.bsList
    default:
      return ctxStore.ref_comp_map[ctxStore.active_comp_id]?.ref_comp_actuals_res_fstmt?.cf_list ?? []
  }
}

export const getSelectedPlanValuesMapByFstmt = (fstmt: StcsFstmtEnum): Record<string, StcsCompActualsResValuesItem> | undefined => {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const selectedPlanData = planningStore.selectedPlanState
  if (!selectedPlanData) {
    return {}
  }
  switch (fstmt) {
    case StcsFstmtEnum.SFSE_PNL:
      return selectedPlanData.valuesMapPnl
    case StcsFstmtEnum.SFSE_BS:
      return selectedPlanData.valuesMapBs
    default:
      return selectedPlanData.valuesMapCf
  }
}

/**
 * Update CTX row in store and set planning store reference
 * Consolidates the logic for updating CTX row data across different financial statements
 */
export function updateCtxRowInStore(ctxRow: PlanningRowData, referenceRowId: string | undefined, fstmt: StcsFstmtEnum) {
  const compId = useCtxStore().active_comp_id
  const planningStore = createPlanningViewStore(compId)

  // Update row in store with CTX metadata
  const rowInStore = getSelectedPlanRowDataByFstmt(fstmt).find((r) => r.row_id === ctxRow.row_id)
  if (rowInStore) {
    rowInStore.level_id = ctxRow.level_id
    rowInStore.fpna_type = ctxRow.fpna_type
    rowInStore.sum_ref = ctxRow.sum_ref
  }

  // Update planning store reference based on financial statement
  if (referenceRowId) {
    switch (fstmt) {
      case StcsFstmtEnum.SFSE_PNL:
        planningStore.setPnlCtxReference(ctxRow.row_id, referenceRowId)
        break
      case StcsFstmtEnum.SFSE_BS:
        planningStore.setBsCtxReference(ctxRow.row_id, referenceRowId)
        break
      default:
        // CashFlow doesn't have CTX references in the current implementation
        break
    }
  }
}

// make a helper fn that will first get all the ctx rows from the planningStore.pnlList and then re-calculate them using the existing fn of recalcCtxRows
export function recalcAllCtxRows() {
  const allowedFpnaTypes = [StcsFpnaTypeEnum.SFT_CTX_LINE_IPO, StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH]

  const rowData1 = getSelectedPlanRowDataByFstmt(StcsFstmtEnum.SFSE_PNL)
  const rowData2 = getSelectedPlanRowDataByFstmt(StcsFstmtEnum.SFSE_BS)
  const ctxRows = [...rowData1, ...rowData2].filter((r) => allowedFpnaTypes.includes(r.fpna_type))

  for (const ctxRow of ctxRows) {
    recalcCtxRows(ctxRow)
  }
}

export function updateCompanyPlanResValues(currentPlanResValues: StcsCompPlanResValues): void {
  const ctxStore = useCtxStore()
  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)

  const { active_comp_id, ref_comp_map } = ctxStore

  // Validate inputs
  // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
  if (!planningStore.selectedPlanID || !active_comp_id || !ref_comp_map[active_comp_id]) {
    consola.error(`[updateCompanyPlanResValues] Invalid inputs: planId=${planningStore.selectedPlanID}, companyId=${active_comp_id}, companyData=${ref_comp_map[active_comp_id]}`)
    return
  }

  // Initialize company plans if undefined
  const companyData = ref_comp_map[active_comp_id]
  companyData.ref_comp_plans = companyData.ref_comp_plans ?? {}

  // Update plan result values with targeted merge
  companyData.ref_comp_plans[planningStore.selectedPlanID] = {
    ...companyData.ref_comp_plans[planningStore.selectedPlanID],
    ref_comp_plan_res_values: {
      ...(companyData.ref_comp_plans[planningStore.selectedPlanID]?.ref_comp_plan_res_values ?? {}),
      ...currentPlanResValues,
    },
  }
}

export function getLevelIdForMethod(method: MethodologyEnum): string {
  switch (method) {
    case MethodologyEnum.MANUAL_DEC_INPUT_IN_CHANGE:
      return 'manual_change_based'
    case MethodologyEnum.MANUAL_DEC_INPUT:
      return 'manual'
    case MethodologyEnum.MONTHLY_GROWTH_RATE:
      return 'monthly_growth_based'
    case MethodologyEnum.ANNUALLY_GROWTH_RATE:
      return 'annual_growth_based'
    default:
      return 'uncategorized' // Fallback for any unhandled cases
  }
}

export function getLabels(option: string): { en: string; de: string } {
  if (Object.prototype.hasOwnProperty.call(translationMap, option)) {
    return (translationMap as Record<string, { en: string; de: string }>)[option]
  }
  // Fallback: generate English label, placeholder for German
  const enLabel = option.replace(/_/g, ' ').toUpperCase()
  return { en: enLabel, de: enLabel } // Replace with proper translation
}

export const getHeaderName = (showAdditionalAutoColumnHeaderText = false, additionalAutoColumnHeaderText?: string) => {
  return showAdditionalAutoColumnHeaderText
    ? `${useCtxStore().ref_comp_map[useCtxStore().active_comp_id].ref_comp.data?.display_name} ${additionalAutoColumnHeaderText}`
    : useCtxStore().ref_comp_map[useCtxStore().active_comp_id].ref_comp.data?.display_name
}

export const getRowIdToUseForUndoRedo = (node: IRowNode<PlanningRowData>): string | undefined => {
  const data = node.data

  const parentRowId = node?.parent?.data?.row_id
  const grandParentId = node?.parent?.parent?.data?.row_id
  const greatGrandParentId = node?.parent?.parent?.parent?.data?.row_id

  // we determine that if this is a sub-position row under a position row
  const isSelectedMethodRow = data?.rowClass === 'selected-method-row' && node?.parent?.data?.rowClass === 'sub-custom-row'
  const isSubPosition = (data?.rowClass === 'sub-custom-row' && parentRowId && grandParentId) || isSelectedMethodRow
  const isSelectedMethodRowButNotSubPosition = data?.rowClass === 'selected-method-row' && node?.parent?.data?.rowClass === 'custom-row'

  let idToUse: string | undefined

  if (isSubPosition && isSelectedMethodRow) {
    idToUse = greatGrandParentId
  } else if ((isSubPosition && !isSelectedMethodRow) || isSelectedMethodRowButNotSubPosition) {
    idToUse = grandParentId
  } else {
    idToUse = parentRowId
  }

  return idToUse
}

/**
 * Find the invert_sum_value by traversing up the row hierarchy
 * First tries to find the position row itself, then looks for any ancestor with invert_sum_value
 */
export function getInvertSumValueFromRowList(positionKey: string, rowList: PlanningRowData[] | undefined): number {
  if (!rowList) return 1

  // First, try to find the position row directly
  const positionRow = rowList.find((r) => r.row_id === positionKey)
  if (positionRow?.options?.invert_sum_value !== undefined) {
    return positionRow.options.invert_sum_value
  }

  // If position row has path_level, traverse ancestors to find invert_sum_value
  if (positionRow?.path_level) {
    // Traverse path_level from end to start (closest ancestor first)
    for (let i = positionRow.path_level.length - 1; i >= 0; i--) {
      const ancestorId = positionRow.path_level[i]
      const ancestorRow = rowList.find((r) => r.row_id === ancestorId)
      if (ancestorRow?.options?.invert_sum_value !== undefined) {
        return ancestorRow.options.invert_sum_value
      }
    }
  }

  // If we can't find the position row, try to find any row that starts with a similar pattern
  // This handles cases where the positionKey might be a child of an existing row
  const matchingRow = rowList.find((r) => positionKey.includes(r.row_id) && r.options?.invert_sum_value !== undefined)
  if (matchingRow?.options?.invert_sum_value !== undefined) {
    return matchingRow.options.invert_sum_value
  }

  return 1
}

/**
 * Check if a column is a month column (col_YYYY_MM format)
 */
export function isMonthColumn(colId: string): boolean {
  return MONTH_COLUMN_REGEX.test(colId)
}

/**
 * Helper function to preserve only actual values (before planning start date) and clear planning values
 * @param valuesMap The values map to filter
 * @param planBeginDateString The planning begin date string in YYYY-MM format
 * @returns A new values map containing only actual period values
 */
export function preserveActualValuesOnly(valuesMap: Record<string, number>, planBeginDateString?: string): Record<string, number> {
  if (!planBeginDateString) return {}

  const planBeginDate = moment(planBeginDateString, 'YYYY-MM')
  const newValuesMap: Record<string, number> = {}

  Object.keys(valuesMap).forEach((monthKey) => {
    const monthDate = moment(monthKey, 'YYYY-MM')
    if (monthDate.isBefore(planBeginDate)) {
      newValuesMap[monthKey] = valuesMap[monthKey]
    }
  })

  return newValuesMap
}

/**
 * Update row status in plan configuration maps (pnl_map or bs_map)
 * @param row_id The row ID to update
 * @param newStatus The new status value ("open" or "done")
 * @param fstmt_type The financial statement type
 */
export function updateRowStatusInPlanCfg(row_id: string, newStatus: 'done' | 'open', fstmt_type: StcsFstmtEnum): void {
  const compId = useCtxStore().active_comp_id
  const planningStore = createPlanningViewStore(compId)

  const planCfg = planningStore.planConfigCache[planningStore.selectedPlanID as string]?.stcs_comp_plan_cfg
  if (!planCfg) return

  const isPnl = fstmt_type === StcsFstmtEnum.SFSE_PNL
  const isDone = cleanString(newStatus ?? '') === 'done'

  if (isPnl) {
    if (planCfg?.pnl_map?.[row_id]) {
      planCfg.pnl_map[row_id].is_done = isDone
    }
  } else {
    // biome-ignore lint/style/useCollapsedElseIf: <explanation>
    if (planCfg?.bs_map?.[row_id]) {
      planCfg.bs_map[row_id].is_done = isDone
    }
  }
}

/**
 * Update row status in the planning store for the appropriate financial statement
 * @param row The row data to update
 * @param newStatus The new status value ("open" or "done")
 */
export function updateRowStatusInStore(row: PlanningRowData, newStatus: 'done' | 'open'): void {
  if (!row) return

  const { row_id, fstmt_type } = row
  if (!row_id) return
  if (!fstmt_type) return

  const compId = useCtxStore().active_comp_id
  const planningStore = createPlanningViewStore(compId)

  const isPnl = fstmt_type === StcsFstmtEnum.SFSE_PNL

  if (isPnl) {
    planningStore.selectedPlanState?.pnlList.forEach((row) => {
      if (row.row_id === row_id) {
        row.status = newStatus
      }
    })
  } else {
    planningStore.selectedPlanState?.bsList.forEach((row) => {
      if (row.row_id === row_id) {
        row.status = newStatus
      }
    })
  }

  // Update status in plan configuration maps
  updateRowStatusInPlanCfg(row_id, newStatus, fstmt_type)
}
